/**
 * Bearer-capability page roots — the URL IS the credential, so it must never
 * reach browser analytics or session replay.
 *   · `/aliado/contactado/<token>` — partner "Ya lo contacté" (2026-09-18).
 *   · `/interesado/<token>` — renter lead-verification answer page
 *   · `/tratos/confirmar/<token>` — host confirms a closed deal (no login)
 *   · `/visita/<token>` — host answers a visit request (no login)
 * The last three were added 2026-09-23 after PostHog showed their tokens
 * arriving through ordinary pageviews (90d: visita 84, tratos/confirmar 25,
 * interesado 12 events). All three are token-only AND uninstrumented, so
 * nothing measured is lost; Datadog client logs go dark on them, as on
 * `/aliado/contactado` — server-side logs still cover their mutations.
 * ⚠️ NOT here, on purpose: `equipo/invitacion`, `inventario/confirmar`,
 * `seguro/inmueble`, `tratos/cierre`, `cobro-directo/cierre` carry funnels /
 * Datadog value that suppression would kill — they get REDACTION instead
 * (`TOKEN_ROUTE_ROOTS` below; docs/claude/instrumentation.md).
 * Every root must stay followed by `/` or end: `/dashboard/host/interesados`
 * (the host inbox, plural) is NOT a capability.
 */
const CAPABILITY_PATHNAME =
  /^\/(?:en\/|es\/)?(?:aliado\/contactado|interesado|tratos\/confirmar|visita)(?:\/|$)/i
const CAPABILITY_RAW =
  /aliado(?:\/|%2f)contactado|tratos(?:\/|%2f)confirmar|(?:^|\/|%2f)(?:interesado|visita)(?:\/|%2f|$)/i
const CAPABILITY_IN_TEXT =
  /(?:\/|%2f)(?:aliado(?:\/|%2f)contactado|tratos(?:\/|%2f)confirmar|interesado|visita)(?:\/|%2f|$)/i

/**
 * REDACTED token pages (2026-09-23) — bearer links whose funnels and
 * monitoring we KEEP: `team_invite_*` + `sign_up` on the invite page,
 * `inventory_confirm_public` / `inmueble_confirm_public`, Datadog on the two
 * month-close rosters. Instead of suppressing telemetry, the token segment is
 * rewritten to `[token]` in everything PostHog / Datadog / Vercel receive, the
 * ad pixels (GA4, Meta, LinkedIn, UET, OpenAI, HubSpot, Ahrefs) never load on
 * them, and crossing into or out of one forces a document navigation so an
 * already-running pixel never observes the URL. Measured before the fix (90d
 * PostHog): equipo/invitacion 1,027 events / 44 token URLs, tratos/cierre
 * 490 / 10, inventario/confirmar 357 / 50.
 */
const TOKEN_ROUTE_ROOTS = [
  'equipo/invitacion',
  'inventario/confirmar',
  'seguro/inmueble',
  'tratos/cierre',
  'cobro-directo/cierre'
]
const rootAlternation = (sep: string): string =>
  TOKEN_ROUTE_ROOTS.map((root) => root.split('/').join(sep)).join('|')
const TOKEN_ROUTE_PATHNAME = new RegExp(
  `^\\/(?:en\\/|es\\/)?(?:${rootAlternation('\\/')})(?:\\/|$)`,
  'i'
)
const SEP = '(?:\\/|%2f)'
/**
 * `<root>/<token>` inside any string (plain or %2f-encoded slashes). Double
 * encoding (`%252F`) is NOT matched — no call site produces it today (URLs,
 * router paths, one-level-encoded `redirectA`/`redirectU`); revisit if one does.
 */
const TOKEN_SEGMENT = new RegExp(
  `(${SEP}(?:${rootAlternation(SEP)})${SEP})(?!\\[token\\])[A-Za-z0-9._~-]+`,
  'gi'
)
const TOKEN_SEGMENT_TEST = new RegExp(TOKEN_SEGMENT.source, 'i')
const REDACTED = '[token]'

/**
 * Firebase email-action links (`/auth/action?mode=resetPassword&oobCode=…`,
 * also verifyEmail / signIn) carry the credential in the QUERY: whoever holds
 * the `oobCode` can set the account's password until it is used or expires.
 * Seen 2026-10-07 in a Datadog client error on a reset page, full code in
 * `view.url`. Same treatment as a token route: redacted in every sink, and
 * `touchesRedactedToken` keeps the ad pixels (which read the raw URL) off the
 * page. `apiKey` in the same link is Firebase's public web key, not a secret.
 */
const OOB_CODE_PARAM =
  /((?:[?&]|%3F|%26)oobCode(?:=|%3D))(?!\[token\])[A-Za-z0-9._~-]+/gi
const OOB_CODE_PARAM_TEST = new RegExp(OOB_CODE_PARAM.source, 'i')

export const isRedactedTokenRoute = (value: string): boolean => {
  try {
    const pathname = decodeURIComponent(
      new URL(value, 'https://spotme.mx').pathname
    )
    return TOKEN_ROUTE_PATHNAME.test(pathname)
  } catch {
    return TOKEN_SEGMENT_TEST.test(value)
  }
}

/**
 * Does this URL touch a redacted-route token ANYWHERE — its path, or its query
 * (plain or %2f-encoded)? The query case is real: `withAuthSSR` sends a
 * logged-out partner from `/tratos/cierre/<token>` to
 * `/authenticate?redirectA=%2Ftratos%2Fcierre%2F<token>`, and KYC forwards
 * it as `redirectU` through the (static) `/userConfirmation/*` steps. Those
 * pages are ordinary routes, so a path check alone let ad pixels and replay
 * record the token there (security review, 2026-09-23). The route itself
 * counts even without a concrete token (conservative); callers pass real
 * URLs — `window.location.href` or a navigation destination.
 */
export const touchesRedactedToken = (value: string): boolean =>
  isRedactedTokenRoute(value) ||
  TOKEN_SEGMENT_TEST.test(value) ||
  OOB_CODE_PARAM_TEST.test(value)

/** Replace every redacted-route token and Firebase `oobCode` with `[token]`. */
export const redactTokenUrl = (value: string): string => {
  const routeRedacted = TOKEN_SEGMENT_TEST.test(value)
    ? value.replace(TOKEN_SEGMENT, `$1${REDACTED}`)
    : value
  return OOB_CODE_PARAM_TEST.test(routeRedacted)
    ? routeRedacted.replace(OOB_CODE_PARAM, `$1${REDACTED}`)
    : routeRedacted
}

/**
 * Deep copy of `value` with every redacted-route token rewritten. Returns the
 * SAME reference when nothing changed, so the common case allocates nothing.
 * `skipKeys` names subtrees left untouched (PostHog's `$snapshot_data`: replay
 * is disabled on token pages, so walking a whole DOM snapshot on every other
 * page would be pure cost).
 */
export const redactTokenUrls = <T>(
  value: T,
  skipKeys: readonly string[] = []
): T => {
  const seen = new WeakSet<object>()
  const visit = (item: unknown): unknown => {
    if (typeof item === 'string') return redactTokenUrl(item)
    if (!item || typeof item !== 'object' || seen.has(item)) return item
    seen.add(item)
    if (Array.isArray(item)) {
      const next = item.map(visit)
      return next.some((v, i) => v !== item[i]) ? next : item
    }
    const entries = Object.entries(item as Record<string, unknown>)
    let changed = false
    const next = entries.map(([k, v]) => {
      if (skipKeys.includes(k)) return [k, v] as const
      const redacted = visit(v)
      if (redacted !== v) changed = true
      return [k, redacted] as const
    })
    return changed ? Object.fromEntries(next) : item
  }
  return visit(value) as T
}

export const isCapabilityRoute = (value: string): boolean => {
  try {
    const pathname = decodeURIComponent(
      new URL(value, 'https://spotme.mx').pathname
    )
    return CAPABILITY_PATHNAME.test(pathname)
  } catch {
    return CAPABILITY_RAW.test(value)
  }
}

/** Also catches a late event's previous URL, referrer, or nested error context. */
export const containsCapabilityUrl = (value: unknown): boolean => {
  const seen = new WeakSet<object>()
  const visit = (item: unknown): boolean => {
    if (typeof item === 'string') {
      let decoded = item
      // Attribution can contain an encoded URL or an encoded path segment.
      for (let depth = 0; depth < 3; depth++) {
        if (CAPABILITY_IN_TEXT.test(decoded)) return true
        try {
          const next = decodeURIComponent(decoded)
          if (next === decoded) return false
          decoded = next
        } catch {
          // Malformed unrelated escapes must not hide an ASCII capability path.
          decoded = decoded.replace(/%[0-9a-f]{2}/gi, (escape) =>
            String.fromCharCode(Number.parseInt(escape.slice(1), 16))
          )
        }
      }
      return false
    }
    if (!item || typeof item !== 'object' || seen.has(item)) return false
    seen.add(item)
    return Object.values(item).some(visit)
  }
  return visit(value)
}

// Sticky for this document only: leaving a capability via SPA must not start
// SDKs with the previous URL in memory. No cookie/localStorage opt-out persists.
let privateDocument = false

export const suppressCapabilityTelemetry = (): void => {
  privateDocument = true
}

export const shouldSuppressCapabilityTelemetry = (
  payload?: unknown
): boolean => {
  if (typeof window !== 'undefined') {
    if (
      isCapabilityRoute(window.location.href) ||
      (typeof document !== 'undefined' && isCapabilityRoute(document.referrer))
    ) {
      privateDocument = true
    }
    if (privateDocument) return true
  }
  return containsCapabilityUrl(payload)
}

/**
 * Called at routeChangeStart, before Next mutates history or emits pageviews.
 * A fresh document is essential: unmounting Scripts cannot unload running SDKs.
 * The generic cancelled error never contains the destination/capability.
 */
export const guardCapabilityNavigation = (
  destination: string,
  current: string,
  navigate: (url: string) => void
): void => {
  const capability =
    isCapabilityRoute(destination) || isCapabilityRoute(current)
  // Redacted token pages: only CROSSING the boundary needs a fresh document
  // (entering: a running GA4/Meta would record the token URL; leaving: ad
  // pixels were never loaded and must load on the next page). Moving within
  // a token document — query/tab changes, or login/KYC carrying the token in
  // `redirectA`/`redirectU` back to the roster — stays a normal SPA transition.
  const crossesTokenBoundary =
    touchesRedactedToken(destination) !== touchesRedactedToken(current)
  if (!capability && !crossesTokenBoundary) return
  if (capability) suppressCapabilityTelemetry()
  navigate(destination)
  throw Object.assign(new Error('Private page requires document navigation'), {
    cancelled: true
  })
}
